import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { FallbackCellType, VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { LegacyExportService } from "@app/directives/_veri/export/legacyExport.service";
import { DateRange } from "@app/enum/DateRange.enum";
import { SortDirection } from "@app/enum/SortDirection";
import { AccountService, IAccount } from "@app/services/account.service";
import { AccountFeatureService } from "@app/services/accountFeature.service";
import { DateService } from "@app/services/date.service";
import { GroupService } from "@app/services/groups/group.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { forkJoin, from } from "rxjs";
import { DynamicReportService, ExportType } from "../../_services/dynamicReports.service";
import { ReportHistoryService } from "../../_services/report-history.service";
import { ReportRunProgressState } from "../../enums/ReportRunProgressState.enum";
import { IReportHistory } from "../../models/IDynamicReportLog";
import { IReportDefinition } from "../../models/IReportDefinition";
import { IReportParameter, ReportParameterType } from "../../models/IReportParameters";
import { ReportHistoryQueryOptions } from "../../models/ReportHistoryQueryOptions";
import { ReportsNavigationModule } from "../../reports.navigation";
import "./report-history-page.component.scss";
import { reportParameterMappings } from "./report-parameter-mappings";

@UntilDestroy()
@Component({
    selector: "report-history-page",
    template: require("./report-history-page.component.html")
})
export class ReportHistoryPageComponent implements OnInit, OnDestroy {
    @Input() reportId: number;
    @Output() reportHistorySelected = new EventEmitter<IReportHistory>();
    loading: boolean;
    reports: IReportDefinition[];
    histories: IReportHistory[];

    selectedHistory: IReportHistory;
    groupParams: { title: string, groupName: string }[];

    defaultDateRange: DateRange = DateRange.ThisMonth;
    startDate: Date;
    endDate: Date;

    tableColumns: IVTableColumn<IReportHistory>[];
    tableRows: IVTableRow[];

    showReportStatus: boolean;
    updateInterval: ReturnType<typeof setInterval>;

    ReportRunProgressState = ReportRunProgressState;
    ExportType = ExportType;
    ReportParameterType = ReportParameterType;

    constructor(
        private readonly legacyExportService: LegacyExportService,
        private readonly vTableService: VTableService,
        private readonly reportService: DynamicReportService,
        private readonly reportHistoryService: ReportHistoryService,
        private readonly accountService: AccountService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly groupService: GroupService,
        private readonly dateService: DateService,
        private readonly navigationService: NavigationService,
        private readonly translationService: TranslationService
    ) {
    }

    ngOnInit() {
        this.createDefaultDates();
        this.updateHistory(true, true);
        this.createTable();
    }

    ngOnDestroy(): void {
        clearInterval(this.updateInterval);
    }

    paramIsObject(paramType: any): boolean {
        return typeof paramType === 'object';
    }

    onRowClicked(row: IVTableRow<IReportHistory>) {
        this.selectedHistory = row.data;
        this.openReportStatus();
    }

    openReportStatus(): void {
        this.getGroupParams();
        this.showReportStatus = true;
    }

    hideReportStatus(): void {
        this.clearSelectedHistory();
        this.showReportStatus = false;
    }

    reloadHistory(): void {
        this.updateHistory(false, true);
    }

    viewReport(): void {
        if (this.reportId) {
            this.reportHistorySelected.emit(this.selectedHistory);
            return;
        }

        this.navigationService.go(ReportsNavigationModule.ViewReport, { historyId: this.selectedHistory.Id, reportId: this.selectedHistory.ReportDefinitionId });
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<IReportHistory>): void {
        if (!filterEvent.trigger) {
            return;
        }

        if (filterEvent.trigger == "date") {
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];
            this.updateHistory(true);
        }
    }

    hasValidStartAndEndDates(): boolean {
        return !this.noDateParams(this.selectedHistory);
    }

    download(fileType: ExportType): void {
        this.legacyExportService.downloadReport(fileType, this.selectedHistory);
    }

    getUnitConversionType(paramType: ReportParameterType): string {
        let unitType: string;

        switch (paramType) {
            case ReportParameterType.MinimumDistance:
                unitType = "distance";
                break;
            case ReportParameterType.MinimumDurationOverWeight:
            case ReportParameterType.MinimumEventTime:
            case ReportParameterType.MinimumIdleTime:
                unitType = "time";
                break;
            case ReportParameterType.MinimumFuelLevelChange:
            case ReportParameterType.MinimumFuelUsed:
                unitType = "capacity";
                break;
            case ReportParameterType.InProgressGeofenceVisits:
                unitType = "none";
                break;
            default:
                unitType = "speed";
                break;
        }

        return unitType;
    }

    paramIsEnum(paramType: ReportParameterType): boolean {
        const enums: ReportParameterType[] = [
            ReportParameterType.ReasonCode,
            ReportParameterType.RoadType,
            ReportParameterType.VehicleType,
            ReportParameterType.GeofenceCategory,
            ReportParameterType.ShowOnlyAlcolockEventGroup,
            ReportParameterType.FilterVehicleRowsByVehicleType
        ];

        return enums.includes(paramType);
    }

    getEnumValue(param: IReportParameter): string {
        const mappings = reportParameterMappings(this.translationService);

        let mapping = mappings.find(mapping => mapping.parameterType === param.ParameterType);

        return mapping.values.find(val => val.Value === param.Value).Name;
    }
    // ---


    private updateHistory(showSpinner = false, invalidateCache = false): void {

        const options: ReportHistoryQueryOptions = {
            Created: { After: new Date(this.startDate).toISOString(), Before: new Date(this.endDate).toISOString() },
            invalidateCache: invalidateCache
        };

        if (this.reportId) {
            options.ReportDefinitionId = this.reportId;
        }

        this.loading = showSpinner;

        forkJoin({
            definitions: from(this.reportService.getReports(invalidateCache) as Promise<IReportDefinition[]>),
            history: this.reportHistoryService.getReportHistory(options),
            account: from(this.accountService.getAccountData() as Promise<IAccount>)
        }).pipe(
            untilDestroyed(this)
        ).subscribe({
            next: data => {
                this.reports = data.definitions.filter(definition => this.accountFeatureService.isReportFeatureEnabledOnAccount(data.account, definition));

                this.histories = data.history;

                if (this.selectedHistory) {
                    this.selectedHistory = this.histories.find(h => h.Id == this.selectedHistory.Id);
                }

                this.createReportRows();

                this.checkForUpdatesRequired();
            },
            complete: () => this.loading = false
        })
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ sorted: false }),
                createCellFunction: (log) => this.vTableService.createStatusCell(this.getReportRowStatus(log))
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Date", sortDirection: SortDirection.Descending, sorted: true }),
                createCellFunction: (log) => this.vTableService.createDateCell(log.CreatedTime),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Name" }),
                createCellFunction: (log) => this.vTableService.createTableCell(log.ReportTitle),
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReportHistory.Source", width: 180 }),
                createCellFunction: (log) => {
                    const scheduledString = this.translationService.t("ReportHistory.Scheduled");
                    const manualString = this.translationService.t("ReportHistory.RunByUser");
                    return this.vTableService.createTableCell(log.ReportScheduleId ? scheduledString : manualString);
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", width: 180 }),
                createCellFunction: log => this.vTableService.createEnumCell(ReportRunProgressState, "ReportRunProgressState", log.ProgressState)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReportSchedules.NextParameters", minTableWidth: 920 }),
                createCellFunction: (log) => {
                    if (this.noDateParams(log)) {
                        return this.vTableService.createDateCell(undefined, { fallbackCellType: FallbackCellType.NA });
                    }

                    const formattedValue = `${this.dateService.formatDate(log.Parameters.Start, { dateTime: true })} - ${this.dateService.formatDate(log.Parameters.End, { dateTime: true })}`

                    return this.vTableService.createTableCell(formattedValue)
                },
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "ReportHistory.Parameters", minTableWidth: 920 }),
                createCellFunction: (log) => {
                    let formattedValue = this.reportHistoryService.createParameterString(log);


                    return this.vTableService.createTableCell(formattedValue, { tooltip: true })
                },
            },
        ];
    }

    private checkForUpdatesRequired(): void {
        //If a report is currently running, we want to check for updates until it has completed.

        clearInterval(this.updateInterval);

        if (!this.histories?.some(history => history.ProgressState !== ReportRunProgressState.Finished)) {
            return;
        }

        this.updateInterval = setInterval(() => {
            this.updateHistory(false, true);
        }, 30_000)
    }

    private getReportRowStatus(log: IReportHistory): VTableRowStatus {

        if (log.ProgressState) {
            if (log.Success) {
                return VTableRowStatus.Success;
            }
            else {
                return VTableRowStatus.Error;
            }
        }

        return VTableRowStatus.Pending;
    }

    private getReportDurationInMinutes(log: IReportHistory): number {
        var t1 = this.dateService.getDurationFromTimeSpan(log.DataCollectionTime);
        var t2 = this.dateService.getDurationFromTimeSpan(log.RowCreationTime);
        var t3 = this.dateService.getDurationFromTimeSpan(log.PopulateRowTime);
        var t4 = this.dateService.getDurationFromTimeSpan(log.DocumentCreationTime);

        var minutes = 0;
        minutes += (t1.hours + t2.hours + t3.hours + t4.hours) * 60;
        minutes += (t1.minutes + t2.minutes + t3.minutes + t4.minutes);
        minutes += (t1.seconds + t2.seconds + t3.seconds + t4.seconds) / 60;

        return minutes;
    }

    private createReportRows() {
        this.tableRows = this.vTableService.createTableRows(this.histories, this.tableColumns);
    }

    private noDateParams(log: IReportHistory): boolean {
        return (new Date(log.Parameters.Start)).getFullYear() < 2000;
    }

    private createDefaultDates(): void {
        const dateRange = this.dateService.dateRangeFromEnum(this.defaultDateRange);
        this.startDate = dateRange.start;
        this.endDate = dateRange.end;
    }

    private clearSelectedHistory(): void {
        this.selectedHistory = null;
    }

    private getGroupParams(): void {
        this.groupParams = [];

        if (!this.selectedHistory) {
            return;
        }

        if (this.selectedHistory.Parameters.VehicleGroupId) {
            this.groupService.getGroup(this.selectedHistory.Parameters.VehicleGroupId).then(group => {
                this.groupParams.push({
                    title: this.translationService.t("ReportHistory.VehicleGroup"),
                    groupName: group.Name
                })
            })
        }

        if (this.selectedHistory.Parameters.DriverGroupId) {
            this.groupService.getGroup(this.selectedHistory.Parameters.DriverGroupId).then(group => {
                this.groupParams.push({
                    title: this.translationService.t("ReportHistory.DriverGroup"),
                    groupName: group.Name
                })
            })
        }

        if (this.selectedHistory.Parameters.GeofenceGroupId) {
            this.groupService.getGroup(this.selectedHistory.Parameters.GeofenceGroupId).then(group => {
                this.groupParams.push({
                    title: this.translationService.t("ReportHistory.GeofenceGroup"),
                    groupName: group.Name
                })
            })
        }
    }
}
